import { FC } from "react";
import styles from "./BillsIncludedModal.module.scss";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import {
  getIconName,
  getPricingCurrency,
  getPricingDuration,
} from "@Pages/PropertyPage/components/RoomTypeSection/utils";
import CircleCheckGreen from "@Icons/CircleCheckGreen";
import CircleCheckGreenDesktop from "@Icons/CircleCheckGreenDesktop";
import { useSelector, shallowEqual } from "react-redux";
import { AppState } from "@Store/index";
import LazyLoadOnScroll from "@Components/LazyLoadOnScroll";
import useWhitelabel from "hooks/useWhitelabel";

interface BillsIncludedDetailProps {
  amenities?: Array<{
    name: string;
    type?: string;
    price?: number;
    description?: string;
    included?: boolean;
  }>;
  isDesktop?: boolean;
  useFourColumnGrid?: boolean;
  totalWeeklyCost?: number;
  dataTestId?: string;
  // description?: string;
}

const BillsIncludedDetail: FC<BillsIncludedDetailProps> = ({
  amenities = [],
  isDesktop = false,
  useFourColumnGrid = false,
  totalWeeklyCost = 0,
  // description = "",
  dataTestId,
}) => {
  const { t } = useTranslationHook();
  const { isWhitelabel } = useWhitelabel();

  const { inventoryCurrency, inventoryLocation, inventoryPricingDuration } = useSelector(
    ({ propertyPageData }: AppState) => ({
      inventoryCurrency: propertyPageData?.data?.pricing?.currency,
      inventoryLocation:
        propertyPageData?.data?.region?.name ||
        propertyPageData?.data?.region?.city ||
        propertyPageData?.data?.region?.combined_name ||
        t("this location"),
      inventoryPricingDuration: propertyPageData?.data?.pricing?.duration,
    }),
    shallowEqual,
  );

  // const getConstantChineseTranslationForDesc = () =>
  //   t("propertypage.components.billsincludeddetail.description", { inventoryLocation });
  const getConstantChineseTranslation = () =>
    `${t("propertypage.instalmentsection.estimatedsavingsstring")} ${inventoryLocation})`;

  const getMobileSavingsText = () =>
    `${t("propertypage.components.amenitiessection.billsincluded")} ${inventoryLocation})*`;
  const currencySymbol = getPricingCurrency(inventoryCurrency) || "£";
  const pricingPeriod = getPricingDuration(inventoryPricingDuration) || t("week");

  // const dynamicDescription = description || getConstantChineseTranslationForDesc();

  const calculatedTotalPrice = amenities.reduce((sum, amenity) => {
    const price = Number(amenity.price);
    const fixedPrice = price && !Number.isNaN(price) ? Math.round(price) : 0;
    return sum + fixedPrice;
  }, 0);

  const displayTotalPrice = calculatedTotalPrice > 0 ? calculatedTotalPrice : totalWeeklyCost;

  const hasEstimatedItems = amenities.some((amenity) => {
    const price = Number(amenity.price);
    return !price || Number.isNaN(price);
  });

  const formatPrice = (price: number) => {
    const numPrice = Number(price);
    return Math.round(numPrice);
  };

  const formatTotalPrice = (price: number) => {
    const numPrice = Number(price);
    const roundedPrice = Math.round(numPrice);
    const isRounded = numPrice !== roundedPrice;
    return { price: roundedPrice, isRounded };
  };

  const { price: totalPrice, isRounded } = formatTotalPrice(displayTotalPrice);
  const tilde = isRounded ? "~" : "";

  const hasNonZeroPrice = amenities.some((amenity) => Number(amenity.price) > 0);

  const formattedTotalPrice = hasEstimatedItems
    ? `${tilde}${currencySymbol}${totalPrice}+/${pricingPeriod}`
    : `${tilde}${currencySymbol}${totalPrice}/${pricingPeriod}`;

  const sortedAmenities = [...amenities].sort((a, b) => {
    const priceA = Number(a.price) || 0;
    const priceB = Number(b.price) || 0;

    if (priceA > 0 && priceB > 0) {
      return priceB - priceA;
    }

    if (priceA > 0 && priceB === 0) return -1;
    if (priceA === 0 && priceB > 0) return 1;

    return 0;
  });

  const renderAmenityItem = (amenity: any, index: number) => {
    const amenityName = amenity.name ?? amenity.type;
    const amenityType = `${amenity.name_en ? amenity.name_en : amenity.name} ${amenity.type}`;
    const renderIcon = getIconName(
      amenityType,
      isDesktop ? "24px" : "24px",
      isDesktop ? "24px" : "24px",
    );

    const amenityPrice = amenity?.price || 0;
    const priceWithCurrency = amenityPrice
      ? `${currencySymbol}${formatPrice(amenityPrice)}/${pricingPeriod}`
      : `${t("Est. N/A")}*`;

    if (isDesktop) {
      return (
        <div key={index} className={styles.desktopAmenityItem}>
          <div
            className={
              isWhitelabel ? styles.whitelabelDesktopIconContainer : styles.desktopIconContainer
            }
          >
            <LazyLoadOnScroll
              noLazy
              fallback={<section style={{ width: "24px", height: "24px" }} />}
            >
              {renderIcon}
            </LazyLoadOnScroll>
          </div>
          <div
            className={`${styles.desktopAmenityContent} ${
              !hasNonZeroPrice ? styles.centeredContent : ""
            }`}
          >
            <div
              className={`${styles.desktopAmenityName} ${
                !hasNonZeroPrice ? styles.centeredName : ""
              }`}
              data-testid={dataTestId}
            >
              {t(amenityName)}
            </div>
            {hasNonZeroPrice && (
              <div className={styles.desktopAmenityMeta}>
                <span className={styles.desktopAmenityPriceSlashed}>
                  {amenityPrice > 0 ? <s>{priceWithCurrency}</s> : priceWithCurrency}
                </span>
                <span className={styles.desktopIncludedIconInline}>
                  <CircleCheckGreenDesktop width="16" height="16" />
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={index} className={styles.amenityItem}>
        <div className={styles.amenityLeft}>
          <div className={isWhitelabel ? styles.whitelabelIconContainer : styles.iconContainer}>
            <LazyLoadOnScroll
              noLazy
              fallback={<section style={{ width: "24px", height: "24px" }} />}
            >
              {renderIcon}
            </LazyLoadOnScroll>
          </div>
          <div
            className={`${styles.amenityText} ${!hasNonZeroPrice ? styles.centeredContent : ""}`}
          >
            <div
              className={`${styles.amenityName} ${!hasNonZeroPrice ? styles.centeredName : ""}`}
              data-testid={dataTestId}
            >
              {t(amenityName)}
            </div>
            {hasNonZeroPrice && (
              <div className={styles.amenityMeta}>
                <span className={styles.amenityPriceSlashed}>
                  {amenityPrice > 0 ? <s>{priceWithCurrency}</s> : priceWithCurrency}
                </span>
                <span className={styles.includedIcon}>
                  <CircleCheckGreen width="12" height="12" />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const desktopGridClass = useFourColumnGrid
    ? styles.desktopAmenityGridFourCol
    : styles.desktopAmenityGrid;

  return (
    <div
      className={`${styles.billsDetailContainer} ${
        isDesktop && useFourColumnGrid ? styles.compactGap : ""
      }`}
    >
      {/* {isDesktop ? <div className={styles.billsDescription}>{dynamicDescription}</div> : []} */}

      {isDesktop ? (
        <div className={desktopGridClass}>
          {sortedAmenities.map((amenity, index) => renderAmenityItem(amenity, index))}
        </div>
      ) : (
        <div className={styles.mobileAmenityGrid}>
          {sortedAmenities.map((amenity, index) => renderAmenityItem(amenity, index))}
        </div>
      )}

      {isDesktop && hasNonZeroPrice && (
        <div className={styles.desktopTotalSectionAlt}>
          <div className={styles.desktopTotalLabel}>{getConstantChineseTranslation()}</div>
          <span className={styles.desktopBannerDivider} />
          <div className={styles.desktopTotalAmount}>
            <span className={styles.desktopFreeTag}>
              {t("propertypage.components.billsincludeddetail.free")}
            </span>
            <span className={styles.desktopTotalPrice}>{formattedTotalPrice}</span>
          </div>
        </div>
      )}
      {!isDesktop && hasNonZeroPrice && (
        <div className={styles.totalSection}>
          <div className={styles.totalLabel}>{getMobileSavingsText()}</div>
          <div className={styles.totalAmount}>
            <span className={styles.totalPrice}>{formattedTotalPrice}</span>
            <span className={styles.freeTag}>
              {t("propertypage.components.billsincludeddetail.free")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(BillsIncludedDetail, {
  componentName: "BillsIncludedDetail-AmenitySection-PropertyPage",
  showForChina: true,
});
